"use client";

import React, { useState } from "react";
import { X, CheckCircle, AlertTriangle } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";

interface GigApplicationModalProps {
    postingId: string;
    gigTitle: string;
    onClose: () => void;
    onSuccess: () => void;
}

type StepType = "form" | "geo_flag" | "success";

export default function GigApplicationModal({ postingId, gigTitle, onClose, onSuccess }: GigApplicationModalProps) {
    const [step, setStep] = useState<StepType>("form");
    const [note, setNote] = useState("");
    const [acknowledge, setAcknowledge] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (geoFlagConfirmed = false) => {
        if (!acknowledge && step === "form") {
            setErrorMessage("Please acknowledge the commitment terms.");
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const url = `${API_BASE}/api/v1/gigs/postings/${postingId}/apply`;
            const res = await fetchWithAuth(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ note, geo_flag_confirmed: geoFlagConfirmed })
            });

            if (res.ok) {
                setStep("success");
            } else {
                const errData = await res.json();
                const detail = errData.detail || "An error occurred.";
                
                if (detail.toLowerCase().includes("geography") || detail.toLowerCase().includes("location")) {
                    setStep("geo_flag");
                } else {
                    setErrorMessage(detail);
                }
            }
        } catch (err) {
            console.error("Application submission failed:", err);
            setErrorMessage("Connection failure. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4 font-sans animate-in fade-in duration-200">
            <div className="bg-[#fcfcfc] border border-gray-200 rounded-xl w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-300">
                {/* Header: spec deep teal header */}
                <div className="p-5 bg-[#004d57] text-white flex items-center justify-between relative">
                    <h4 className="font-sans font-black text-sm uppercase tracking-wider text-white">
                        {step === "success" ? "Application Confirmed" : "Express Interest"}
                    </h4>
                    {step !== "success" && (
                        <button 
                            onClick={onClose}
                            className="bg-white/10 hover:bg-white/20 border-none text-white w-[26px] h-[26px] rounded-md transition-all flex items-center justify-center cursor-pointer font-bold"
                            aria-label="Close"
                        >
                            ×
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 text-slate-700 bg-white">
                    {errorMessage && (
                        <div className="bg-rose-50 text-rose-800 p-3.5 rounded-lg text-xs border border-rose-100 font-semibold leading-relaxed">
                            {errorMessage}
                        </div>
                    )}

                    {step === "form" && (
                        <div className="space-y-4">
                            <div className="space-y-1 bg-gray-50 border border-gray-100 rounded-lg p-3">
                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Opportunity</label>
                                <p className="text-xs sm:text-sm font-bold text-[#004d57] leading-snug">{gigTitle}</p>
                            </div>

                            <div className="space-y-1">
                                <label htmlFor="note-textarea" className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Statement of Interest (Optional)</label>
                                <textarea
                                    id="note-textarea"
                                    rows={3}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Explain why you want to serve in this gig and how your background aligns..."
                                    className="w-full border border-gray-250 rounded-lg p-3 text-xs sm:text-sm focus:outline-none focus:border-[#007a8a] bg-white transition-colors leading-relaxed placeholder:text-gray-300 resize-none"
                                />
                            </div>

                            <label className="flex items-start gap-3 select-none cursor-pointer group mt-2">
                                <input
                                    type="checkbox"
                                    checked={acknowledge}
                                    onChange={(e) => setAcknowledge(e.target.checked)}
                                    className="mt-1 accent-[#007a8a]"
                                />
                                <span className="text-[11px] text-gray-500 leading-relaxed group-hover:text-gray-700 transition-colors">
                                    I acknowledge the time commitment and cadence required for this volunteer gig. I confirm that I satisfy the eligibility guidelines.
                                </span>
                            </label>

                            <button
                                onClick={() => handleSubmit(false)}
                                disabled={isSubmitting}
                                className="w-full bg-[#007a8a] hover:bg-[#006977] disabled:bg-gray-100 disabled:text-gray-400 text-white py-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all shadow-sm border-none cursor-pointer flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? "Submitting..." : "Submit Application"}
                            </button>
                        </div>
                    )}

                    {step === "geo_flag" && (
                        <div className="space-y-4 text-center py-2">
                            <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto border border-amber-100">
                                <AlertTriangle className="w-6 h-6" />
                            </div>
                            <h5 className="text-sm font-bold text-[#003d45] uppercase tracking-wider">Geographic Mismatch</h5>
                            <p className="text-xs text-gray-500 leading-relaxed max-w-sm mx-auto">
                                We noticed this gig has specific regional scopes that might not match your profile settings. You can still confirm if you are able to participate remotely or as a one-off.
                            </p>
                            <div className="flex flex-col gap-2 pt-2">
                                <button
                                    onClick={() => {
                                        setStep("form");
                                        setErrorMessage("Please verify your regional scope in Account Settings.");
                                    }}
                                    className="bg-gray-150 hover:bg-gray-200 text-gray-700 py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all border-none cursor-pointer"
                                >
                                    Correct My Address
                                </button>
                                <button
                                    onClick={() => handleSubmit(true)}
                                    disabled={isSubmitting}
                                    className="bg-[#007a8a] hover:bg-[#006977] text-white py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-all border-none cursor-pointer"
                                >
                                    Confirm One-Off & Apply
                                </button>
                            </div>
                        </div>
                    )}

                    {step === "success" && (
                        <div className="space-y-4 text-center py-4">
                            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-100">
                                <CheckCircle className="w-9 h-9" />
                            </div>
                            <h5 className="text-base font-bold text-[#003d45] uppercase tracking-wider">Application Received</h5>
                            <p className="text-xs text-gray-500 leading-relaxed max-w-sm mx-auto">
                                Thank you for volunteering! Your interest has been logged and the group organizers have been notified. You can track this application in your dashboard.
                            </p>
                            <button
                                onClick={onSuccess}
                                className="w-full bg-[#003d45] hover:bg-[#002a30] text-white py-3 rounded-xl text-xs font-bold uppercase tracking-widest transition-all border-none cursor-pointer"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

